import React, {
  ComponentType,
  Dispatch,
  Fragment,
  SetStateAction,
} from 'react';
import * as S from './styles';
import Image from 'next/image';

import logo_chippu_place from '@/assets/logo_chippu_place.svg';
import { useTheme } from 'styled-components';
import Header, { HeaderProps } from '@/components/atoms/Header';
import IconButton from '@/components/atoms/IconButton';
import { usePathname, useRouter } from 'next/navigation';
import { useSacola } from '@/hooks/useSacola';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import SearchField from '@/components/molecules/SearchField';
import { IconeAjuda } from '@/components/atoms/Icones/IconeAjuda';
import Text from '@/components/atoms/Text';
import Button from '@/components/atoms/Button';
import Avatar from '@/components/atoms/Avatar';
import { IconeSeta } from '@/components/atoms/Icones/IconeSeta';
import DropdownMenu, {
  DropdownMenuItem,
} from '@/components/molecules/DropdownMenu';
import { IconProps } from '@/types/icon';
import { User } from 'next-auth';
import { IconeCracha } from '@/components/atoms/Icones/IconeCracha';
import { IconeDeslogar } from '@/components/atoms/Icones/IconeDeslogar';
import useBreakpoint from '@/hooks/useBreakpoint';
import { IconePessoa } from '@/components/atoms/Icones/IconePessoa';
import obterIniciaisNomeUsuario from '@/utils/obterIniciaisNomeUsuario';
import { registrarEvento } from '@/services/googleAnalytics';
import useResumoCompraHook from '@/components/organisms/ResumoCompra/hook/useResumoCompraHook';
import useAutenticacaoAcoes from '@/hooks/useAutenticacaoACoes';
import { IconeSacola } from '@/components/atoms/Icones/IconeSacola';
import { IconeCarteira } from '@/components/atoms/Icones/IconeCarteira';

type CabecalhoHomeProps = HeaderProps & {
  $valorInputPesquisa: string;
  $setarValorInputPesquisa: Dispatch<SetStateAction<string>>;
  $setarInputAtivo: Dispatch<SetStateAction<boolean>>;
};

const BotaoPaginasUsuario = ({
  Icone,
  tamanhoIcone,
  corIcone,
  tituloPagina,
  onClickBotao,
}: {
  Icone: ComponentType<IconProps>;
  tamanhoIcone: number;
  corIcone: string;
  tituloPagina: string;
  onClickBotao: () => void;
}) => {
  return (
    <DropdownMenuItem $size={2} key={tituloPagina} onSelect={onClickBotao}>
      <S.ContainerBotaoPaginasUsuario>
        <Icone size={tamanhoIcone} color={corIcone} />
        <Text
          $size={2}
          $color="neutral"
          $weight="semibold"
          $align="left"
          $highContrast={true}
          $text={tituloPagina}
        />
      </S.ContainerBotaoPaginasUsuario>
    </DropdownMenuItem>
  );
};

export default function CabecalhoHomeDesktop(props: CabecalhoHomeProps) {
  const { $valorInputPesquisa, $setarValorInputPesquisa, $setarInputAtivo } =
    props;
  const { breakpoint } = useBreakpoint();
  const theme = useTheme();
  const { status, data } = useSession();
  const usuarioLogado: User | null = data?.user ?? null;
  const usuarioPossuiImagem = !!usuarioLogado?.image;

  const router = useRouter();
  const caminhoURL = usePathname();

  const { itens } = useSacola();

  const { valoresResumoCompra } = useResumoCompraHook();

  const { deslogar } = useAutenticacaoAcoes();

  return (
    <Header
      {...props}
      $style={{
        paddingTop: 0,
        paddingBottom: 0,
        flexDirection: 'row',
        flex: 1,
        justifyContent: 'center',
      }}
    >
      <S.ToolBar>
        <Link
          href="/"
          style={{
            display: 'flex',
            alignSelf: 'center',
          }}
          onClick={() => $setarValorInputPesquisa('')}
        >
          <Image
            src={logo_chippu_place}
            alt="Chippu Place logo"
            height={theme.typography.font.size[7]}
            priority
          />
        </Link>
        {breakpoint === 'mobile' && caminhoURL === '/pagamento-cartao' ? (
          <div />
        ) : (
          <S.ContainerSearchField>
            <SearchField
              onChange={(e) => {
                $setarValorInputPesquisa(e?.target?.value);
              }}
              $size={1}
              $variant="surface"
              $state={$valorInputPesquisa.length > 0 ? 'filled' : 'placeholder'}
              $label={false}
              $focusRing={false}
              $placeholder="Buscar por produtos, planos e ofertas"
              $inputProps={{
                value: $valorInputPesquisa,
                onFocus: () => $setarInputAtivo(true),
                onBlur: () => $setarInputAtivo(false),
              }}
            />
          </S.ContainerSearchField>
        )}
        {status !== 'authenticated' ? (
          <Link href="/logar">
            <S.ContainerAutenticacao>
              <Avatar
                $size={3}
                $iconColor={theme.tokens.colors.black_contrast}
                $iconSize={theme.typography.font.size[6]}
                $color="neutral"
                $shape="rounded"
                $variant="solid"
                $preencherFundo={true}
                $Icon={IconePessoa}
              />
              <S.Coluna>
                <S.Linha>
                  <Text
                    $size={2}
                    $color="neutral"
                    $weight="bold"
                    $align="left"
                    $highContrast={true}
                    $text="Entre"
                  />
                  <Text
                    $size={2}
                    $color="neutral"
                    $weight="medium"
                    $align="left"
                    $highContrast={true}
                    $text="ou"
                  />
                </S.Linha>
                <Text
                  $size={2}
                  $color="neutral"
                  $weight="bold"
                  $align="left"
                  $highContrast={true}
                  $text="Cadastre-se"
                />
              </S.Coluna>
            </S.ContainerAutenticacao>
          </Link>
        ) : (
          <S.ContainerAutenticacao>
            <Avatar
              $size={3}
              $shape="rounded"
              $variant="solid"
              $color="accent"
              $image={usuarioLogado?.image ?? undefined}
              $initials={
                !usuarioPossuiImagem
                  ? obterIniciaisNomeUsuario(usuarioLogado?.name ?? '')
                  : undefined
              }
              $initialsColor={
                !usuarioPossuiImagem
                  ? theme.tokens.colors.black_contrast
                  : undefined
              }
              $preencherFundo={!usuarioPossuiImagem ? true : undefined}
            />
            <S.Coluna>
              <Text
                $size={2}
                $color="neutral"
                $weight="bold"
                $align="left"
                $highContrast={true}
                $text={usuarioLogado?.name}
                style={{
                  paddingLeft: theme.spacing[2],
                  display: 'block',
                  maxWidth: '15vw',
                }}
                $quebraTexto={false}
                $utilizarReticencias
              />
              <DropdownMenu
                $size={1}
                $variant="solid"
                $highContrast={false}
                $type="default"
                $alignOffset={5}
                $sideOffset={2}
                $triggerDropdownMenu={
                  <Button
                    $size={1}
                    $variant="ghost"
                    $color="accent"
                    $highContrast={false}
                    $loading={false}
                    $focusRing={false}
                    $text="Minha conta"
                    $IconEnd={IconeSeta}
                    $iconEndProps={{
                      direcao: 'baixo',
                    }}
                    style={{
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                  />
                }
                $funcaoParaExecutarAposAbertura={() => {
                  registrarEvento('cliqueBotaoMinhaContaCabecalhoDesktop', {});
                }}
                $contentDropdownMenu={
                  <Fragment>
                    <BotaoPaginasUsuario
                      tituloPagina="Meu perfil"
                      Icone={IconeCracha}
                      corIcone={theme.tokens.colors.text}
                      onClickBotao={() => {
                        registrarEvento(
                          'cliqueCabecalhoDesktopIrParaMeuPerfil',
                          {},
                        );
                        router.push('/minha-conta/meu-perfil', {
                          scroll: true,
                        });
                      }}
                      tamanhoIcone={theme.typography.font.size[6]}
                    />
                    <BotaoPaginasUsuario
                      tituloPagina="Minhas compras"
                      Icone={IconeSacola}
                      corIcone={theme.tokens.colors.text}
                      onClickBotao={() => {
                        registrarEvento(
                          'cliqueCabecalhoDesktopIrParaMinhaCompras',
                          {},
                        );
                        router.push('/minha-conta/minhas-compras', {
                          scroll: true,
                        });
                      }}
                      tamanhoIcone={theme.typography.font.size[6]}
                    />
                    <BotaoPaginasUsuario
                      tituloPagina="Minha carteira"
                      Icone={IconeCarteira}
                      corIcone={theme.colors.default.white}
                      onClickBotao={() => {
                        registrarEvento(
                          'cliqueCabecalhoDesktopIrParaMinhaCarteira',
                          {},
                        );
                        router.push('/minha-conta/minha-carteira', {
                          scroll: true,
                        });
                      }}
                      tamanhoIcone={theme.typography.font.size[6]}
                    />
                    <BotaoPaginasUsuario
                      tituloPagina="Sair"
                      Icone={IconeDeslogar}
                      corIcone={theme.tokens.colors.text}
                      onClickBotao={() => deslogar('/')}
                      tamanhoIcone={theme.typography.font.size[6]}
                    />
                  </Fragment>
                }
                style={{
                  width: 183,
                  maxHeight: 250,
                }}
              />
            </S.Coluna>
          </S.ContainerAutenticacao>
        )}
        <IconButton
          $size={2}
          $variant="ghost"
          $color="neutral"
          $highContrast={true}
          $loading={false}
          $state="default"
          $focusRing={false}
          $Icon={IconeAjuda}
          $onClick={() => router.push('/ajuda', { scroll: true })}
        />
        <IconButton
          $size={2}
          $variant="ghost"
          $color="neutral"
          $highContrast={true}
          $loading={false}
          $state="default"
          $focusRing={false}
          $Icon={IconeSacola}
          $onClick={() => {
            registrarEvento('view_cart', {
              currency: 'BRL',
              value: valoresResumoCompra?.valorTotal,
              items: itens.map((itemSacola) => {
                if (
                  itemSacola?.produto &&
                  itemSacola?.opcaoSelecionadaProduto
                ) {
                  return {
                    item_id: itemSacola?.opcaoSelecionadaProduto?.id,
                    item_brand: itemSacola?.produto?.nome,
                    item_name: itemSacola?.opcaoSelecionadaProduto?.nome,
                    discount: itemSacola?.opcaoSelecionadaProduto?.desconto,
                    quantity: itemSacola.quantidade,
                  };
                } else if (itemSacola?.plano) {
                  return {
                    item_id: itemSacola.plano.comboID,
                    item_name: itemSacola.plano.nome,
                    discount: itemSacola.plano.desconto,
                  };
                }
              }),
            });
            router.push(
              `/sacola${caminhoURL.includes('/minha-conta') ? '?voltarParaTelaAssinatura=true' : ''}`,
              { scroll: true },
            );
          }}
          $textoBadge={itens.length == 0 ? undefined : itens.length.toString()}
        />
      </S.ToolBar>
    </Header>
  );
}
